import { useState, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { isTestEnv } from "../globals";

const useFetchWithMsal = (msalRequest) => {
  const { instance, accounts } = useMsal();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const activeAccount = accounts[0];

  const execute = async (...args) => {
    if (!activeAccount) {
      const error = new Error("No active account");
      setError(error);
      throw error;
    }
    
    let response;
    let responseData;
    
    try {
      await instance.initialize();
      
      let result;
      try {
        result = await instance.acquireTokenSilent({
          ...msalRequest,
          account: activeAccount,
        });
      } catch (silentError) {
        if (silentError instanceof InteractionRequiredAuthError) {
          isTestEnv && console.log("Token expired, attempting interactive login");
          
          result = await instance.acquireTokenRedirect({
            ...msalRequest,
            account: activeAccount,
          });
        } else {
          throw silentError;
        }
      }

      let method, endpoint, reqData;
      [method, endpoint, reqData] = args;
      
      const headers = new Headers();
      const bearer = `Bearer ${result.accessToken}`;
      headers.append("Authorization", bearer);

      if (!(reqData instanceof FormData)) {
        headers.append("Content-Type", "application/json");
      }

      const requestOptions = {
        method: method,
        headers: headers,
        body: reqData ? (reqData instanceof FormData ? reqData : JSON.stringify(reqData)) : null,
      };

      setIsLoading(true);
      isTestEnv && console.log("Sending request:", {
        endpoint,
        method,
        headers: Object.fromEntries(headers),
        isFormData: reqData instanceof FormData
      });

      response = await fetch(endpoint, requestOptions);

      if (response.status >= 400) {
        const text = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(text);
        } catch {
          errorData = { message: text };
        }

        throw {
          status: response.status,
          msg: errorData.message || "An error occurred",
          data: errorData
        };
      }

      responseData = await response.json();
      setData(responseData);
      setIsLoading(false);
      return responseData;

    } catch (error) {
      setError(error);
      setIsLoading(false);
      throw error;
    }
  };

  return {
    isLoading,
    error,
    data,
    execute: useCallback(execute, [activeAccount, instance]),
  };
};

export default useFetchWithMsal;